import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { Card, CardBody, Row, Col, Button, Spinner } from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { liveFirestore, ensureLiveAuth } from "../../../helpers/firebase_live";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const mapContainerStyle = {
  width: "100%",
  height: "70vh",
};

const defaultCenter = {
  lat: 41.0082,
  lng: 28.9784,
};

const JobLiveLocation = () => {
  const { id } = useParams();
  const mapRef = useRef(null);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapType, setMapType] = useState("satellite");
  const [error, setError] = useState(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  useEffect(() => {
    if (!id) return;

    setLoading(true);
    setError(null);

    const docRef = liveFirestore.collection("live_locations").doc(`mission_${id}`);

    // Firestore kuralları kimlik doğrulaması istiyor; dinlemeyi oturum açıldıktan
    // sonra başlatıyoruz. Bu arada bileşen sökülürse bayrak aboneliği engeller.
    let iptalEdildi = false;
    let unsubscribe = () => {};

    ensureLiveAuth().then(() => {
      if (iptalEdildi) return;

      unsubscribe = docRef.onSnapshot(
        (snapshot) => {
          if (snapshot.exists) {
            const data = snapshot.data();
            setLocation({
              lat: data.lat,
              lng: data.lng,
              speed: data.speed,
              driverName: data.driverName,
              updatedAt: data.updatedAt,
              isActive: data.isActive,
              status: data.status,
            });
          } else {
            setError("Bu iş için canlı konum verisi bulunamadı.");
            setLocation(null);
          }
          setLoading(false);
        },
        (err) => {
          console.error("Firestore dinleme hatası:", err);
          setError("Konum verisi alınırken bir hata oluştu.");
          setLoading(false);
        }
      );
    });

    return () => {
      iptalEdildi = true;
      unsubscribe();
    };
  }, [id]);

  const handleMapLoad = (map) => {
    mapRef.current = map;
  };

  useEffect(() => {
    if (mapRef.current && location) {
      mapRef.current.panTo({ lat: location.lat, lng: location.lng });
    }
  }, [location]);

  const breadcrumbItems = [
    { title: "İşler", link: "/job-list" },
    { title: `Canlı Konum (#${id})`, link: "#" },
  ];

  const lastUpdateText = location?.updatedAt
    ? new Date(location.updatedAt).toLocaleString("tr-TR")
    : "-";

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs
            title={`Canlı Konum Takibi — İş #${id}`}
            breadcrumbItems={breadcrumbItems}
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h5 className="card-title mb-1">Sürücü Konumu</h5>
                      {location?.driverName && (
                        <p className="text-muted mb-0">
                          Sürücü: <strong>{location.driverName}</strong>
                        </p>
                      )}
                    </div>
                    <div className="d-flex gap-2">
                      <Button
                        color="secondary"
                        outline
                        size="sm"
                        onClick={() =>
                          setMapType((prev) =>
                            prev === "satellite" ? "roadmap" : "satellite"
                          )
                        }
                      >
                        {mapType === "satellite" ? "🗺️ Harita" : "🛰️ Uydu"}
                      </Button>
                      <Link to={`/job-details/${id}`}>
                        <Button color="info" size="sm">
                          İş Detayları
                        </Button>
                      </Link>
                    </div>
                  </div>

                  {loading && (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted">Konum verisi yükleniyor...</p>
                    </div>
                  )}

                  {!loading && error && (
                    <div className="alert alert-warning" role="alert">
                      <i className="bx bx-error-circle me-2"></i>
                      {error}
                    </div>
                  )}

                  {isLoaded && !loading && (
                    <GoogleMap
                      mapContainerStyle={mapContainerStyle}
                      center={
                        location
                          ? { lat: location.lat, lng: location.lng }
                          : defaultCenter
                      }
                      zoom={16}
                      mapTypeId={mapType}
                      onLoad={handleMapLoad}
                      options={{
                        streetViewControl: false,
                        fullscreenControl: false,
                        mapTypeControl: false,
                      }}
                    >
                      {location && (
                        <Marker
                          position={{
                            lat: location.lat,
                            lng: location.lng,
                          }}
                          icon={{
                            url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
                            scaledSize: new window.google.maps.Size(40, 40),
                          }}
                          title={location.driverName || "Sürücü"}
                        />
                      )}
                    </GoogleMap>
                  )}

                  {!isLoaded && !loading && (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted">Harita yükleniyor...</p>
                    </div>
                  )}

                  {location && (
                    <div className="mt-3 d-flex flex-wrap gap-3">
                      <div className="border rounded p-2">
                        <small className="text-muted d-block">Enlem</small>
                        <strong>{location.lat?.toFixed(6)}</strong>
                      </div>
                      <div className="border rounded p-2">
                        <small className="text-muted d-block">Boylam</small>
                        <strong>{location.lng?.toFixed(6)}</strong>
                      </div>
                      <div className="border rounded p-2">
                        <small className="text-muted d-block">Hız</small>
                        <strong>
                          {location.speed != null ? `${location.speed.toFixed(1)} km/h` : "-"}
                        </strong>
                      </div>
                      <div className="border rounded p-2">
                        <small className="text-muted d-block">Son Güncelleme</small>
                        <strong>{lastUpdateText}</strong>
                      </div>
                      <div className="border rounded p-2">
                        <small className="text-muted d-block">Durum</small>
                        <strong>
                          {location.status === "ACTIVE"
                            ? "Aktif"
                            : location.status === "END_REQUESTED"
                            ? "Sonlandırma İsteği"
                            : location.status === "ENDED"
                            ? "Sonlandırıldı"
                            : location.status || "Bilinmiyor"}
                        </strong>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default JobLiveLocation;
